// ─── Shared Workspace design tokens ─────────────────────────────────────────
//
// Before this file existed, OutlookWorkspace.js, ComposeWindow.js, and
// FileMenu.js each hard-coded their own separate `const C = {...}` color
// object — three independent copies of essentially the same Outlook-blue
// palette, with different key names for the same concepts (e.g.
// OutlookWorkspace's `sidebar` === FileMenu's `bg`, both "#f3f2f1"). Any
// future palette tweak had to be made in three places by hand.
//
// This module is the single source of truth. Structurally, every key below
// is unchanged from the original consolidation (same names, same call
// sites) — nothing here required touching OutlookWorkspace.js, MailMerge.js,
// FileMenu.js, or SequenceBuilder.js again; they all keep importing the same
// names and get the new colors automatically.
//
// ─── Palette: "Lightning" (Salesforce-inspired) ─────────────────────────
// Requested reference: Salesforce's own Lightning Design System — dark navy
// structural chrome, a blue primary/brand color, light-gray app background,
// white cards, and Salesforce's actual semantic palette (blue/green/orange/
// red/purple) rather than a single accent. One deliberate deviation from
// Salesforce's literal hex values: Salesforce's brand blue (#0176D3) sits
// almost exactly on top of Microsoft's Fluent blue (#0078D4) — same hue,
// same saturation, three points of lightness apart, effectively
// indistinguishable at a glance. Since the original brief explicitly ruled
// out looking like Microsoft, `accent` here is pulled deeper/more navy
// (#0B5CAB) than Salesforce's literal blue — still unmistakably "that blue,"
// just richer and clearly not a Fluent-blue relative. Everything else
// (the dark navy header `nav`/`navDark`, light-gray `sidebar`/`bg` app
// background, white cards, `border` gray, and the green/orange/red/purple
// status colors) are close to Salesforce's actual Lightning Design System
// tokens.
export const WORKSPACE_THEME = {
  nav:"#032D60", navDark:"#16325C", navText:"#fff",
  ribbon:"#ffffff", ribbonBorder:"#DDDBDA", ribbonHover:"#F3F3F3", ribbonActive:"#EAF2FC",
  sidebar:"#F3F3F3", sidebarBorder:"#DDDBDA", sidebarHover:"#EAEAEA",
  sidebarSel:"#EAF2FC", sidebarSelText:"#0B5CAB", sidebarSelBorder:"#0B5CAB",
  list:"#ffffff", listHover:"#FAFAF9", listSel:"#EAF2FC", listBorder:"#DDDBDA",
  reading:"#ffffff",
  accent:"#0B5CAB", accentL:"#EAF2FC",
  green:"#2E844A", greenL:"#E4F5E8",
  amber:"#DD7A01", amberL:"#FCEEE0",
  red:"#BA0517", redL:"#FDE7E9",
  purple:"#5A3EA1", purpleL:"#EFEAF9",
  gold:"#B98412", goldL:"#FBF0DC",
  text:"#16161A", textSub:"#514F4D", textMuted:"#706E6B",
  border:"#DDDBDA", white:"#fff",
  // Aliases used only by FileMenu.js today — same values as above under the
  // names it already called them, so its render output stays in lockstep
  // with the rest of the app rather than drifting out of sync.
  bg:"#F3F3F3",          // === sidebar
  hover:"#F3F3F3",       // === sidebar
  sel:"#EAF2FC",         // === sidebarSel / ribbonActive
  borderLight:"#DDDBDA", // === listBorder
};

// A small spacing/sizing scale for FUTURE ribbon/toolbar work (P1+) to
// build on — intentionally not applied anywhere yet in this pass, since
// retrofitting every existing inline style to use it would be a much
// larger, higher-risk diff for zero visible change today. Kept here so the
// next phase has a home for it instead of inventing its own local scale.
export const WORKSPACE_SPACING = {
  ribbonGroupGap: 4,
  ribbonGroupPadding: "4px 8px",
  iconSm: 15,
  iconMd: 17,
  iconLg: 20,
};

// A small corner-radius scale for the ribbon redesign — rounder, softer
// corners across ribbon buttons/tabs/panels/menus than the original flat
// Office-2016-style 2-4px values, aiming for a more modern "Fluent 2 /
// Linear" professional feel. `sm` is for compact controls (dense ribbon
// buttons, menu items); `md` for default-size ribbon buttons and tab tops;
// `lg` for the ribbon's own floating panel and the dropdown menus that hang
// off it; `pill` for fully-round chips/badges. Centralized here (rather
// than inlined at each call site) so Workspace's and Mail Merge's ribbons —
// which share RibbonPrimitives.js — stay visually in lockstep as this
// scale evolves.
export const WORKSPACE_RADIUS = {
  sm: 6,
  md: 10,
  lg: 14,
  pill: 999,
};
// Note (Phase 1 design-system foundation, see linear-brewing-toast.md): md
// and lg already line up with the new shared C-token-based primitives —
// ui/ContextMenu.js uses 10, ui/Modal.js uses 14 — so no value changes were
// needed here for those to feel consistent with the rest of the app.

// ─── Shared shadow scale (Phase 1 design-system foundation) ────────────────
// Added alongside the same pass that built the shared Modal/Tooltip/
// ContextMenu primitives (ui/Modal.js, ui/Tooltip.js, ui/ContextMenu.js) and
// extended ui/theme.js's C palette — see linear-brewing-toast.md.
// WORKSPACE_THEME had no shared shadow scale before this: every hand-rolled
// dropdown/modal/context-menu in OutlookWorkspace.js and MailMerge.js picks
// its own box-shadow value inline (mostly a flat
// "0 4px 16px rgba(0,0,0,.18)"). This is the same soft, layered shadow
// language the new C-token-based primitives use (see ui/Modal.js's card
// shadow, ui/ContextMenu.js's menu shadow) given a home here so a later
// pass migrating Workspace/Mail Merge's own popups has a shared scale to
// move onto instead of each call site continuing to pick its own value.
// Purely additive — nothing existing references these yet, so nothing
// currently rendered changes as part of this file.
export const WORKSPACE_SHADOWS = {
  sm: "0 2px 6px rgba(0,0,0,.08)",                               // small floating controls (tooltips, tiny menus)
  md: "0 4px 14px rgba(0,0,0,.10), 0 1px 2px rgba(0,0,0,.04)",   // dropdown menus, context menus
  lg: "0 20px 60px rgba(0,0,0,.20), 0 2px 8px rgba(0,0,0,.08)",  // modals/sheets
  card: "0 1px 2px rgba(0,0,0,.03), 0 8px 22px rgba(0,0,0,.04)", // resting cards/panels — matches the Apple-styled Dashboard cards in App.js
};

// ─── Sequence's design tokens ───────────────────────────────────────────────
//
// SequenceBuilder.js (and, via its `theme` prop, RotationTestingPanel.js /
// SubjectRotationPanel.js when rendered from Sequence) previously used a
// genuinely different blue-gray palette from WORKSPACE_THEME — a deliberate
// second language at the time. Now that the whole point of this pass is
// making all three modules read as one cohesive product, Sequence's
// neutrals (`text`/`textSub`/`textMuted`/`border`) are harmonized onto the
// exact same warm ink/paper/border values Workspace uses, while `bg`/
// `bgCard`/`bgHover`/`bgDeep` stay Sequence's own slightly-lighter-weight
// surface tones (a subtler distinction than before, not none) so it doesn't
// look like a byte-for-byte re-skin of Workspace's chrome. `accent`/`green`/
// `amber`/`red`/`purple` still reference WORKSPACE_THEME directly, so they
// track any future palette change automatically. `cyan` is Sequence's own
// extra categorical color (used only to color-code one of seven rotating
// step badges — see STEP_COLORS in SequenceBuilder.js) — that's a
// functional need (seven steps need to look visually distinct from each
// other in a row of badges), not brand chrome, so it's the one place a
// genuinely different hue is still deliberately kept — set here to a teal
// in the same family as Salesforce's own extended chart palette.
export const SEQUENCE_THEME = {
  bg:"#FAFAF9", bgCard:"#FFFFFF", bgHover:"#F3F3F3", bgDeep:"#EAEAEA",
  border:"#DDDBDA", accent:WORKSPACE_THEME.accent, accentMuted:"#EAF2FC",
  green:WORKSPACE_THEME.green, greenMuted:"#E4F5E8",
  amber:WORKSPACE_THEME.amber, amberMuted:"#FCEEE0",
  red:WORKSPACE_THEME.red, redMuted:"#FDE7E9",
  purple:WORKSPACE_THEME.purple, purpleMuted:"#EFEAF9",
  cyan:"#0B827C",
  text:"#16161A", textSub:"#514F4D", textMuted:"#706E6B",
};

// ─── Shared status-chip color mapping (Phase 5) ─────────────────────────────
//
// Before this existed, "what color means this is currently sending" was
// answered three separate times: Mail Merge's MailMergeRun status badge
// (Draft/Sending/Paused/Completed/Canceled), Sequence's Monitor view
// (active/completed/stopped_replied/stopped_suppressed/stopped_manual —
// its own local ENROLLMENT_STATUS map), and Workspace's account connection
// indicator (Connected/Syncing/Offline). Each domain keeps its own raw
// status strings and human labels — those genuinely differ — but the
// COLOR a given kind of state gets is now defined once, here, as a small
// set of semantic buckets. Callers map their own status string to a
// bucket key and supply their own label text to <StatusChip>.
// Neutral buckets (draft/loading/stopped) use a visibly-darker-than-page
// gray ("#E1DFDD", not "#F3F3F3") deliberately — "#F3F3F3" is
// WORKSPACE_THEME.bg itself, and several callers render this chip inside a
// container already set to that exact background, so a chip using the same
// value would be invisible against it (found and fixed during Phase 5
// verification: Mail Merge's Draft status chip disappeared against its own
// modal background before this change).
export const STATUS_CHIP_COLORS = {
  active:   { bg: WORKSPACE_THEME.accent+"1a", tc: WORKSPACE_THEME.accent }, // in-progress/connected/enrolled
  sending:  { bg: WORKSPACE_THEME.accent+"1a", tc: WORKSPACE_THEME.accent }, // actively sending right now
  paused:   { bg: "#FCEEE0", tc: WORKSPACE_THEME.amber },
  loading:  { bg: "#E1DFDD", tc: "#605E5C" },
  completed:{ bg: "#E4F5E8", tc: WORKSPACE_THEME.green },
  replied:  { bg: "#EFEAF9", tc: WORKSPACE_THEME.purple }, // "reply-detected" — someone engaged
  error:    { bg: "#FDE7E9", tc: WORKSPACE_THEME.red },
  stopped:  { bg: "#E1DFDD", tc: "#605E5C" }, // manually/otherwise stopped, no error implied
  draft:    { bg: "#E1DFDD", tc: "#605E5C" },
};

export default WORKSPACE_THEME;
